/**
 * How a month cell is coloured — shared by the person page's month grid (PersonDetail)
 * and the Overview's per-employee month grid (EmployeeMonthGrid), so the two read a
 * cell the same way (review #697, 2026-10-16).
 */
import { MIN_COHORT } from '@/measures/peer'

/**
 * What one month cell is coloured against (review #697, 2026-10-16: "should be off
 * peer average"):
 *
 *  - a scored row: the scorecard's own peer bar for that month (`monthScorer.barFor`) —
 *    the bar the cell's points are earned on, and for the rates and volumes the
 *    "Peer average" in the person page's Seven measures. Until #697 these rows were
 *    coloured against the plain mean of every job-title holder's monthly figure, which
 *    a few thin months drag a long way (Claims billed vs resolved: 326% that month
 *    against a peer average of 71%), so a cell could earn its points and still read red;
 *  - an unscored row: the job title's average for that month, five or more with a figure;
 *  - otherwise the person's own average for the range.
 *
 * EmployeeMonthGrid uses only the first step: its unscored metrics keep the job
 * title's plain monthly mean (`monthGridReference`).
 */
export function monthCellReference(
  scoredBar: number | null,
  titleValues: readonly number[],
  own: number | null,
): { ref: number | null; basis: 'peer' | 'title' | 'own' } {
  if (scoredBar !== null && Number.isFinite(scoredBar)) return { ref: scoredBar, basis: 'peer' }
  if (titleValues.length >= MIN_COHORT)
    return { ref: titleValues.reduce((a, b) => a + b, 0) / titleValues.length, basis: 'title' }
  return { ref: own, basis: 'own' }
}

/**
 * Colour one month against its reference (`monthCellReference`). Within 10% of it is
 * neutral, direction-aware either side.
 */
export function cellTone(
  value: number | null,
  avg: number | null,
  higherIsBetter: boolean,
): { bg: string; fg: string } {
  if (value === null || avg === null || avg === 0)
    return { bg: 'transparent', fg: 'var(--ink-3)' }
  const r = value / avg
  if (r > 0.9 && r < 1.1) return { bg: 'transparent', fg: 'var(--ink)' }
  const better = higherIsBetter ? r >= 1.1 : r <= 0.9
  return better
    ? { bg: 'var(--good-bg)', fg: 'var(--good-ink)' }
    : { bg: 'var(--crit-bg)', fg: 'var(--crit-ink)' }
}
